import React from "react";
import _ from "lodash";
import { notify } from "react-notify-toast";
import queryString from "query-string";
import { NetworkAPI } from "apis";

import LoadingUI from "components/common/LoadingUI";
import PageTitle from "components/common/PageTitle";
import NetworkInfoHeader from "components/ops-mgmt/common/NetworkInfoHeader";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import ItemsFilter from "components/ops-mgmt/common/ItemsFilter";
import CustomFirstLookDiffView from "./CustomFirstLookDiffView";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import PromptModal from "components/common/PromptModal";
import SimpleDataTable from "components/common/SimpleDataTable";
import Checkbox from "components/common/Checkbox";
import NotesInformation from "components/common/NotesInformation";

import FirstLookSettingTable from "./FirstLookSettingTable";
import CustomFirstLookSettingModal from "./CustomFirstLookSettingModal";
import PreviewValidateModal from "./PreviewValidateModal";
import ChangeHistoryButton from "./ChangeHistoryButton";
import BatchUpdateButton from "./BatchUpdateButton";
import EditCustomFirstLookDataModal from "./EditCustomFirstLookDataModal";
import NetworkEnsureResourcesButton from "../NetworkEnsureResources";
import CustomRuleJsonEditModal from "./CustomRuleJsonEditModal";

import { setQueryString } from "helpers/QueryString";
import { buttonActionClassGray } from "helpers/StyleClass";
import { MESSAGE } from "constants/Message";

import { MODAL_TYPE, TYPE } from "./Constants";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import { CUSTOM_TYPE } from "../custom-upr/Constants";
import {
  cleanupRule,
  validateFirstLook,
  validateRuleCustom,
} from "helpers/CustomUprFirstLookUtils";
import { setFirstLookValueByPath } from "helpers/Rollback";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 whitespace-pre shadow hover:bg-gray-300 text-gray-900 font-semibold";

const buttonDisabledClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-200 whitespace-pre text-gray-400 font-semibold cursor-not-allowed";

const SELECT_ACTION = {
  ENABLE: "enable",
  DISABLE: "disable",
  REMOVE: "remove",
};

class NetworkFirstLookSettingViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSaving: false,
      isLoading: true,
      errMsg: null,
      saveErrMsg: null,

      isModalOpen: false,
      modalType: MODAL_TYPE.ADD,
      currentRule: null,
      currentIdx: -1,

      placements: null,
      units: null,
      virtualPlacements: null,
      networkId: null,
      networkInfo: null,

      firstLookCurrency: null,
      etag: null,
      notes: null,
      updatedAt: null,
      originalRules: null, // save original for diff
      customRules: null,
      resultTypeRules: [],
      selectedIds: [],
      selectedType: null,
      inputValue: "",

      isPromptModalOpen: false,
      promptHeader: null,
      promptMessage: null,
      promptAction: null,
      promptMsg: [],
      isDisabledHidden: true,

      importIdList: [],
      popupId: "",

      isEditDataModalOpen: false,
      isPreviewValidateModalOpen: false,
    };

    this.searchRef = React.createRef();
    this.batchRef = React.createRef();
    this.changeLogRef = React.createRef();

    this.handleFilterSearch = this.handleFilterSearch.bind(this);

    this.handleValidate = this.handleValidate.bind(this);
    this.handleSaveCustomFL = this.handleSaveCustomFL.bind(this);
    this.handleOpenAdd = this.handleOpenAdd.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
    this.handleToggleDisabled = this.handleToggleDisabled.bind(this);

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleAddRule = this.handleAddRule.bind(this);
    this.handleEditRule = this.handleEditRule.bind(this);
    this.handlePriorityChange = this.handlePriorityChange.bind(this);
    this.handleRollBack = this.handleRollBack.bind(this);

    this.handleOpenEdit = this.handleOpenEdit.bind(this);
    this.handleOpenEditJSON = this.handleOpenEditJSON.bind(this);
    this.handleOpenDuplicate = this.handleOpenDuplicate.bind(this);
    this.handleOpenChangeLog = this.handleOpenChangeLog.bind(this);
    this.handleRemoveRule = this.handleRemoveRule.bind(this);
    this.handleToggleStatus = this.handleToggleStatus.bind(this);
    this.handleValidateRule = this.handleValidateRule.bind(this);

    this.handleReplaceCurrentConfig =
      this.handleReplaceCurrentConfig.bind(this);
    this.handleEditCurrentConfig = this.handleEditCurrentConfig.bind(this);
    this.handleGetRefDataByType = this.handleGetRefDataByType.bind(this);

    this.handleSelectedAction = this.handleSelectedAction.bind(this);
    this.handleBatchUpdateRules = this.handleBatchUpdateRules.bind(this);
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      const { networkId } = this.props.match.params;
      if (!networkId) {
        throw new Error("Missing Network ID");
      }
      const networkInfo = await NetworkAPI.getNetworkInfo({ networkId });
      if (!networkInfo) {
        throw new Error("Invalid Network");
      }

      const { customSettings, updatedAt, etag, firstLookCurrency, notes } =
        await NetworkAPI.getNetworkCustomFirstLook({ networkId });

      const { placement } = await NetworkAPI.getNetworkResourceMapping({
        networkId,
      });

      const units = await NetworkAPI.getUnitsByNetworkId({ networkId });
      const virtualPlacements = await NetworkAPI.getNetworkVirtualPlacements({
        networkId,
      });

      const { importId } = queryString.parse(this.props.location.search);

      const selectedType = _.find(
        _.values(TYPE),
        (key) => (customSettings[key] || []).length > 0
      );

      this.setState(
        {
          networkId,
          networkInfo,
          placements: placement ? placement.mapping : [],
          units,
          virtualPlacements,

          originalRules: _.cloneDeep(customSettings || []),
          customRules: _.cloneDeep(customSettings || []),
          selectedType: selectedType || TYPE.DISPLAY,

          updatedAt,
          etag,
          notes,
          firstLookCurrency,

          isLoading: false,
          importIdList: importId ? importId.split(",") : [],
        },
        () => {
          this.handleFilterSearch();
          if (importId) this.handleOpenAdd();
        }
      );
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { customRules } = this.state;
    if (!_.isEqual(customRules, prevState.customRules)) {
      this.handleFilterSearch();
    }
  }

  handleFilterSearch() {
    const { inputValue, customRules, selectedType, isDisabledHidden } =
      this.state;
    // Search rule name
    const searchedRules = (customRules[selectedType] || []).filter(
      (rule) =>
        inputValue === "" ||
        rule.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Filter Disable and Enabled rules
    const filteredRules = searchedRules.filter((rule) => {
      if (isDisabledHidden) {
        return rule.enabled !== false;
      }
      return true;
    });

    this.setState({
      resultTypeRules: filteredRules,
    });
  }

  handleClearSearch() {
    if (this.searchRef.current) {
      this.searchRef.current.handleClearInput();
      this.setState({ inputValue: "" }, () => this.handleFilterSearch());
    }
  }

  handleToggleDisabled() {
    const { isDisabledHidden } = this.state;
    this.setState({ isDisabledHidden: !isDisabledHidden }, () =>
      this.handleFilterSearch()
    );
  }

  handleOpenAdd() {
    const { selectedType } = this.state;
    this.setState({
      isModalOpen: true,
      modalType: MODAL_TYPE.ADD,
      currentRule: {
        enabled: true,
        type: selectedType,
        targetingSection: {},
        priceSection: {
          priceSettings: [],
        },
      },
    });
  }

  handleCloseModal() {
    this.setState({
      isModalOpen: false,
      modalType: null,
      currentRule: null,
      importIdList: [],
    });
  }

  handleAddRule({ rule, id, idx }) {
    const type = rule.type;

    const ruleList = this.state.customRules[type] || [];

    this.setState(
      {
        customRules: {
          ...this.state.customRules,
          [type]:
            idx >= ruleList.length || rule.enabled === false
              ? [...ruleList, rule]
              : this._moveItem([...ruleList, rule], ruleList.length, idx),
        },
        selectedType: type,
        importIdList: _.filter(this.state.importIdList, (i) => i !== id),
      },
      () => {
        if (id) {
          setQueryString(this.props, {
            importId:
              this.state.importIdList.length === 0
                ? undefined
                : this.state.importIdList,
          });
        }
      }
    );
  }

  handleEditRule({ rule, idx }) {
    const { customRules } = this.state;
    const type = rule.type;
    const itemIdx = _.findIndex(
      customRules[type] || [],
      (r) => r.id === rule.id
    );
    const newList = _.cloneDeep(customRules[type] || []);
    newList[itemIdx] = rule;

    this.setState({
      isModalOpen: false,
      customRules: {
        ...this.state.customRules,
        [type]: idx >= 0 ? this._moveItem(newList, itemIdx, idx) : newList,
      },
    });
  }

  handleOpenEdit(rule) {
    this.setState({
      isModalOpen: true,
      modalType: MODAL_TYPE.EDIT,
      currentRule: rule,
      currentIdx:
        _.findIndex(
          _.filter(
            this.state.customRules[rule.type],
            (d) => d.enabled !== false
          ),
          (r) => r.id === rule.id
        ) + 1,
    });
  }

  handleOpenEditJSON(rule) {
    this.setState({
      isModalOpen: true,
      currentRule: rule,
      modalType: MODAL_TYPE.JSON,
    });
  }

  handleOpenDuplicate(rule) {
    this.setState({
      isModalOpen: true,
      modalType: MODAL_TYPE.COPY,
      currentRule: rule,
    });
  }

  handleOpenChangeLog(rule) {
    if (this.changeLogRef.current) {
      this.changeLogRef.current.handleOpenRule({
        rule,
        type: this.state.selectedType,
      });
    }
  }

  handleValidateRule(rule) {
    const { customRules, modalType, currentRule } = this.state;
    const newRule = cleanupRule(rule);

    if ([MODAL_TYPE.EDIT, MODAL_TYPE.JSON].includes(modalType)) {
      if (newRule.id && newRule.id !== currentRule.id) {
        return ["Rule ID cannot be changed.", MESSAGE.ERROR];
      }

      if (newRule.type && newRule.type !== currentRule.type) {
        return ["Rule Type cannot be changed.", MESSAGE.ERROR];
      }
    }

    const errMsg = validateFirstLook(
      newRule,
      [MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType),
      customRules[newRule.type]
    );

    if (errMsg !== "") {
      return [errMsg, MESSAGE.ERROR];
    }

    const isPass = validateRuleCustom(newRule);
    if (!isPass) {
      return ["Customs section is empty.", MESSAGE.WARNING];
    }

    return [];
  }

  handleRemoveRule(rule, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Remove Rule",
        promptMessage: (
          <>
            Are you sure you want to remove the rule? <br />
            <div
              className="my-2 overflow-y-auto"
              style={{ maxHeight: "20rem" }}
            >
              <SimpleDataTable
                items={[rule]}
                columns={[
                  { key: "id", label: "ID", className: "text-right" },
                  {
                    key: "name",
                    label: "Name",
                    className: "text-right",
                    style: { width: "70%" },
                  },
                ]}
              ></SimpleDataTable>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleRemoveRule(rule, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }
    const { customRules } = this.state;
    const type = rule.type;
    const list = (customRules[type] || []).filter((r) => r.id !== rule.id);
    let result = _.cloneDeep(customRules);
    if (list.length === 0) {
      result = _.omit(result, type);
    } else {
      result[type] = list;
    }

    this.setState({
      customRules: result,
    });
  }

  handleToggleStatus(rule) {
    const { customRules } = this.state;
    const type = rule.type;
    const itemIdx = _.findIndex(
      customRules[type] || [],
      (r) => r.id === rule.id
    );
    const newList = _.cloneDeep(customRules[type] || []);
    newList[itemIdx] = rule;

    this.setState({
      customRules: {
        ...customRules,
        [type]: newList,
      },
    });
  }

  handleSelectedAction(action, isPrompt = true) {
    const { selectedIds, customRules, selectedType } = this.state;

    if (isPrompt) {
      const selectedList = _.map(selectedIds, (id) => {
        return (
          _.find(customRules[selectedType], (r) => {
            return r.id === id;
          }) || {}
        );
      });
      this.setState({
        isPromptModalOpen: true,
        promptHeader: `${_.startCase(action)} Selected Rules`,
        promptMessage: (
          <>
            Are you sure you want to ${action} the {selectedList.length}{" "}
            selected rules? <br />
            <div
              className="my-2 overflow-y-auto"
              style={{ maxHeight: "20rem" }}
            >
              <SimpleDataTable
                items={selectedList}
                columns={[
                  { key: "id", label: "ID", className: "text-right" },
                  {
                    key: "name",
                    label: "Name",
                    className: "text-right",
                    style: { width: "70%" },
                  },
                ]}
              ></SimpleDataTable>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleSelectedAction(action, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    let newRules = _.cloneDeep(customRules[selectedType]);
    if (action === SELECT_ACTION.REMOVE) {
      newRules = _.filter(newRules, (r) => {
        return selectedIds.indexOf(r.id) === -1;
      });
    } else if (action === SELECT_ACTION.ENABLE) {
      newRules = _.map(newRules, (r) => {
        if (selectedIds.indexOf(r.id) !== -1) {
          return { ...r, enabled: true };
        }
        return r;
      });
    } else if (action === SELECT_ACTION.DISABLE) {
      newRules = _.map(newRules, (r) => {
        if (selectedIds.indexOf(r.id) !== -1) {
          return { ...r, enabled: false };
        }
        return r;
      });
    }

    this.setState({
      customRules: { ...customRules, [selectedType]: newRules },
      selectedIds: [],
    });
  }

  handleBatchUpdateRules(payload, isPrompt = true) {
    const { customRules, selectedType } = this.state;

    const { selected, content, type } = payload;

    if (isPrompt) {
      const selectedList = _.map(selected, (id) => {
        return (
          _.find(customRules[selectedType], (r) => {
            return r.id === id;
          }) || {}
        );
      });
      this.setState({
        isPromptModalOpen: true,
        promptHeader: `Update ${_.startCase(type)} Selected Rules`,
        promptMessage: (
          <>
            Are you sure you want to update {type} the {selectedList.length}{" "}
            selected rules? <br />
            <div
              className="my-2 overflow-y-auto"
              style={{ maxHeight: "20rem" }}
            >
              <SimpleDataTable
                items={selectedList}
                columns={[
                  { key: "id", label: "ID", className: "text-right" },
                  {
                    key: "name",
                    label: "Name",
                    className: "text-right",
                    style: { width: "70%" },
                  },
                ]}
              ></SimpleDataTable>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleBatchUpdateRules(payload, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    let newRules = _.cloneDeep(customRules[selectedType]);
    if (type === "label") {
      _.forEach(selected, (id) => {
        const idx = _.findIndex(newRules, (r) => r.id === id);
        if (idx !== -1) {
          newRules[idx] = { ...newRules[idx], label: content.label };
        }
      });
    }

    this.setState(
      {
        customRules: { ...customRules, [selectedType]: newRules },
        selectedIds: [],
      },
      () => {
        if (this.batchRef.current) {
          this.batchRef.current.handleCloseModal();
        }
      }
    );
  }

  handlePriorityChange(id, priority) {
    const { customRules, selectedType } = this.state;
    const itemIdx = _.findIndex(
      customRules[selectedType] || [],
      (r) => r.id === id
    );
    const newList = _.cloneDeep(customRules[selectedType] || []);

    this.setState({
      customRules: {
        ...customRules,
        [selectedType]: this._moveItem(newList, itemIdx, priority - 1),
      },
    });
  }

  handleRollBack({ configType, path, from, to }) {
    const { customRules, originalRules } = this.state;

    if (
      _.isNil(originalRules[configType]) ||
      _.isNil(customRules[configType])
    ) {
      this.setState((prevState) => ({
        customRules: {
          ...prevState.customRules,
          [configType]: prevState.originalRules[configType]
            ? _.cloneDeep(prevState.originalRules[configType])
            : undefined,
        },
      }));
    } else {
      if (_.isNil(path)) return;

      const newRule = setFirstLookValueByPath({
        original: originalRules[configType],
        changed: customRules[configType],
        path,
        from,
        to,
      });
      this.setState({
        customRules: {
          ...customRules,
          [configType]: newRule,
        },
      });
    }
  }

  async handleSaveCustomFL(notesValue) {
    const { networkId, customRules, etag } = this.state;
    this.setState({ isSaving: true });
    try {
      const { result } = await NetworkAPI.updateNetworkCustomFirstLook({
        networkId,
        customSettings: customRules,
        etag,
        notes: notesValue,
      });
      if (result.op === "ok" || result.op === "OK") {
        this.setState({
          saveErrMsg: null,
          originalRules: _.cloneDeep(customRules), // Clear diffs to prevent beforeunload alert
        });
        notify.show("Rule updated! Reloading page...", "success");
        setTimeout(() => {
          window.location.reload(false);
        }, 3000);
      } else {
        throw `Failed response: ${JSON.stringify(result)}`;
      }
    } catch (err) {
      this.setState({ saveErrMsg: err.toString() });
    }
    this.setState({ isSaving: false });
  }

  handleReplaceCurrentConfig(data, isPrompt = true) {
    const { customRules, currentRule, networkInfo, selectedType } = this.state;
    const { config, historyId, createdAt, isSpecificRule, cb } = data;

    let parsedConfig = config;
    if (isSpecificRule) {
      const changedRule = config[selectedType][0];
      const changedList = _.isNil(changedRule)
        ? _.filter(customRules[selectedType], (r) => r.id !== currentRule.id)
        : _.map(customRules[selectedType], (r) => {
            if (r.id === changedRule.id) {
              return changedRule;
            }
            return r;
          });
      parsedConfig = {
        ...customRules,
        [selectedType]: changedList.length > 0 ? changedList : undefined,
      };
    }

    const refDataByType = this.handleGetRefDataByType();

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Replace Current Config",
        promptMessage: (
          <>
            <div className="text-sm">The selected config is:</div>
            <div className="mb-1 flex items-center gap-1 font-semibold text-blue-600">
              {historyId}
              {" - "}
              <DateTimeFormatter
                datetime={createdAt}
                timezone={networkInfo.timezone}
                place="right"
              ></DateTimeFormatter>
            </div>
            <div className="text-sm">
              And the following changes will be made to your current custom
              first look rule.
            </div>
            <div className="bg-gray-100 px-2 py-1">
              <CustomFirstLookDiffView
                originalRules={customRules}
                newRules={parsedConfig}
                refData={refDataByType}
                isInModal={true}
              ></CustomFirstLookDiffView>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleReplaceCurrentConfig(data, false);
        },
      });
      return;
    }

    if (cb) cb();
    this.setState(
      {
        customRules: parsedConfig,
        promptMsg: ["Replace current config success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleEditCurrentConfig(config, isPrompt = true) {
    const { customRules } = this.state;

    const refDataByType = this.handleGetRefDataByType();

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Replace Current Config",
        promptMessage: (
          <>
            <div className="bg-gray-100 px-2 py-1">
              <CustomFirstLookDiffView
                refData={refDataByType}
                originalRules={customRules}
                newRules={config}
              ></CustomFirstLookDiffView>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleEditCurrentConfig(config, false);
        },
      });
      return;
    }

    this.setState(
      {
        customRules: config,
        isEditDataModalOpen: false,
        promptMsg: ["Edit current config success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleGetRefDataByType() {
    const { customRules, originalRules } = this.state;
    return _.values(TYPE).reduce((acc, type) => {
      acc[type] = _.unionBy(
        _.get(customRules, type, []),
        _.get(originalRules, type, []),
        "id"
      );
      return acc;
    }, {});
  }

  async handleValidate(config) {
    return await NetworkAPI.validateCustomFirstLookSetting({
      setting: config,
    });
  }

  _moveItem(list, from, to) {
    let clone = _.clone(list);
    const enabledList = _.filter(clone, (item) => item.enabled !== false);
    const toItem = enabledList[to];
    const newToIdx = _.findIndex(clone, { id: toItem.id });

    const [item] = clone.splice(from, 1);
    const targetIndex = Math.max(0, Math.min(newToIdx, clone.length));

    clone.splice(targetIndex, 0, item);
    return clone;
  }

  render() {
    const {
      isLoading,
      isSaving,
      errMsg,

      isModalOpen,
      modalType,
      currentRule,
      currentIdx,

      firstLookCurrency,
      networkId,
      networkInfo,
      placements,
      units,
      virtualPlacements,
      notes,

      originalRules,
      customRules,
      updatedAt,
      saveErrMsg,
      selectedIds,
      selectedType,
      inputValue,

      resultTypeRules,
      popupId,

      isPromptModalOpen,
      promptHeader,
      promptMessage,
      promptAction,
      promptMsg,
      isDisabledHidden,

      importIdList,
      isEditDataModalOpen,
      isPreviewValidateModalOpen,
    } = this.state;

    const selectedRules = selectedType ? customRules[selectedType] || [] : [];

    const allDisabledNum = _.filter(
      selectedRules,
      (r) => r.enabled === false
    ).length;

    const allEnabledNum = _.filter(
      selectedRules,
      (r) => r.enabled !== false
    ).length;

    const filteredDisabledNum = _.filter(
      resultTypeRules,
      (r) => r.enabled === false
    ).length;

    const filteredEnabledNum = _.filter(
      resultTypeRules,
      (r) => r.enabled !== false
    ).length;

    const refDataByType = this.handleGetRefDataByType();

    return (
      <div>
        {isLoading && <LoadingUI></LoadingUI>}
        {errMsg && <div className="text-red-800">{errMsg}</div>}

        {networkInfo && (
          <div>
            <NetworkInfoHeader networkInfo={networkInfo}></NetworkInfoHeader>

            <div className="min-h-screen bg-gray-200 px-12 py-8">
              <div className="flex w-3/4 items-center justify-between pr-2">
                <PageTitle title="Network Custom First Look Rule"></PageTitle>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={buttonActionClass}
                    onClick={() => this.setState({ isEditDataModalOpen: true })}
                  >
                    Edit Custom JSON
                  </button>
                  <ChangeHistoryButton
                    ref={this.changeLogRef}
                    refData={refDataByType}
                    networkInfo={networkInfo}
                    networkId={networkId}
                    handleReplaceData={this.handleReplaceCurrentConfig}
                  />
                </div>
              </div>

              <div className="flex w-full">
                <div className="my-2 w-3/4 pr-2">
                  <div className="text-sm font-medium">Notes</div>
                  <NotesInformation notes={notes} />
                  <div className="rounded mt-2 flex items-center justify-between bg-white p-4">
                    <div className="flex gap-2">
                      <BatchUpdateButton
                        ref={this.batchRef}
                        rules={customRules[selectedType] || []}
                        selected={selectedIds}
                        handleBatchUpdate={this.handleBatchUpdateRules}
                      ></BatchUpdateButton>
                      <ActionButtonList
                        selectedIds={selectedIds}
                        handleSelectedRemove={() =>
                          this.handleSelectedAction(SELECT_ACTION.REMOVE)
                        }
                        handleSelectedEnable={() =>
                          this.handleSelectedAction(SELECT_ACTION.ENABLE)
                        }
                        handleSelectedDisable={() =>
                          this.handleSelectedAction(SELECT_ACTION.DISABLE)
                        }
                      ></ActionButtonList>
                    </div>
                    <button
                      type="button"
                      className="shadow rounded ml-auto bg-blue-200 px-4 py-2 font-medium text-blue-800 hover:bg-blue-300"
                      onClick={this.handleOpenAdd}
                    >
                      Add custom rule
                    </button>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center text-sm">
                    {_.values(TYPE).map((type) => {
                      return (
                        <button
                          key={type}
                          type="button"
                          className={`border rounded mb-1 mr-1 border-gray-200 ${
                            selectedType === type ? "bg-blue-200" : "bg-white"
                          }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                          onClick={() => {
                            this.setState(
                              {
                                selectedType: type,
                                popupId: "",
                              },
                              () => this.handleFilterSearch()
                            );
                          }}
                        >
                          {type} ({(customRules[type] || []).length})
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ marginBottom: "600px" }}>
                    <div className="mt-2 flex items-center gap-4 text-gray-800">
                      <div>
                        <span className="font-semibold">
                          Total: {customRules.length} rules
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="flex items-center gap-1 text-green-600">
                          <FiCheckCircle></FiCheckCircle> Enabled
                        </span>
                        : {allEnabledNum}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="flex items-center gap-1 text-red-600">
                          <FiXCircle></FiXCircle> Disabled
                        </span>
                        : {allDisabledNum}
                      </div>
                      <label className="rounded ml-4 flex cursor-pointer items-center py-2">
                        <Checkbox
                          isChecked={isDisabledHidden}
                          onChange={this.handleToggleDisabled}
                        ></Checkbox>
                        <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
                          Hide Disabled
                        </span>
                      </label>
                    </div>

                    <div className="mb-2 flex w-2/3 gap-2">
                      <div className="w-3/4">
                        <ItemsFilter
                          ref={this.searchRef}
                          handleSearch={(value) =>
                            this.setState({ inputValue: value }, () =>
                              this.handleFilterSearch()
                            )
                          }
                          placeholder="Search by Rule Name"
                        ></ItemsFilter>
                      </div>
                      <div className="p-1">
                        <button
                          type="button"
                          className={buttonActionClassGray}
                          onClick={this.handleClearSearch}
                        >
                          Clear Search
                        </button>
                      </div>
                    </div>

                    <div className="mb-2 font-medium text-gray-900">
                      Showing {resultTypeRules.length} out of{" "}
                      {selectedRules.length} rules ({filteredEnabledNum}{" "}
                      enabled, {filteredDisabledNum} disabled)
                    </div>

                    <FirstLookSettingTable
                      popupId={popupId}
                      isSearch={inputValue !== ""}
                      networkId={networkId}
                      allItems={selectedRules}
                      items={resultTypeRules}
                      networkInfo={networkInfo}
                      currency={firstLookCurrency}
                      units={units}
                      virtualPlacements={virtualPlacements}
                      placements={placements}
                      selectedItems={selectedIds}
                      customRules={customRules[selectedType] || []}
                      handleSelect={(list) =>
                        this.setState({ selectedIds: list })
                      }
                      handleOpenEdit={this.handleOpenEdit}
                      handleOpenEditJSON={this.handleOpenEditJSON}
                      handleOpenDuplicate={this.handleOpenDuplicate}
                      handleOpenChangeLog={this.handleOpenChangeLog}
                      handleRemoveRule={this.handleRemoveRule}
                      handlePriorityChange={this.handlePriorityChange}
                      handleToggleStatus={this.handleToggleStatus}
                      handlePopupIdChange={(id) =>
                        this.setState({ popupId: id })
                      }
                    ></FirstLookSettingTable>
                  </div>
                </div>

                {!_.isEmpty(customRules) && (
                  <div className="w-1/4 pl-2">
                    <div className="text-sm font-semibold text-gray-700">
                      <div className="text-gray-600">Updated at:</div>
                      <DateTimeFormatter
                        datetime={updatedAt}
                        timezone={networkInfo.timezone}
                      ></DateTimeFormatter>
                    </div>

                    <div className="mt-4 flex justify-center">
                      {isSaving ? (
                        <div className="loader"></div>
                      ) : (
                        <button
                          type="button"
                          className="shadow rounded bg-blue-400 px-8 py-3 font-semibold text-white hover:bg-blue-600"
                          onClick={() =>
                            this.setState({ isPreviewValidateModalOpen: true })
                          }
                        >
                          Preview and Save
                        </button>
                      )}
                    </div>
                    <div className="break-all font-semibold text-red-600">
                      {saveErrMsg}
                    </div>

                    <div className="mt-4 py-4">
                      <CustomFirstLookDiffView
                        refData={refDataByType}
                        originalRules={originalRules}
                        newRules={customRules}
                        handleRollBack={this.handleRollBack}
                      ></CustomFirstLookDiffView>
                    </div>

                    <div className="mt-4 flex items-center justify-center bg-gray-100 p-4">
                      <NetworkEnsureResourcesButton
                        networkId={networkId}
                        name={networkInfo.name}
                      ></NetworkEnsureResourcesButton>
                    </div>
                  </div>
                )}

                <SelectedStickyFooter
                  idList={["id"]}
                  selectedItems={selectedIds}
                  handleSelectedByKey={() => {}}
                  handleClear={() => this.setState({ selectedIds: [] })}
                  actionButtons={
                    <div className="flex gap-2">
                      <ActionButtonList
                        selectedIds={selectedIds}
                        handleSelectedRemove={() =>
                          this.handleSelectedAction(SELECT_ACTION.REMOVE)
                        }
                        handleSelectedEnable={() =>
                          this.handleSelectedAction(SELECT_ACTION.ENABLE)
                        }
                        handleSelectedDisable={() =>
                          this.handleSelectedAction(SELECT_ACTION.DISABLE)
                        }
                      ></ActionButtonList>
                    </div>
                  }
                ></SelectedStickyFooter>
              </div>

              {isModalOpen && modalType !== MODAL_TYPE.JSON && (
                <CustomFirstLookSettingModal
                  networkId={networkId}
                  networkInfo={networkInfo}
                  currency={firstLookCurrency}
                  currentRule={currentRule}
                  currentIdx={currentIdx}
                  customRules={customRules}
                  placements={placements}
                  isModalOpen={isModalOpen}
                  modalType={modalType}
                  importIdList={importIdList}
                  handleValidate={this.handleValidateRule}
                  handleCloseModal={this.handleCloseModal}
                  handleAddRule={this.handleAddRule}
                  handleEditRule={this.handleEditRule}
                ></CustomFirstLookSettingModal>
              )}

              {isModalOpen && modalType === MODAL_TYPE.JSON && (
                <CustomRuleJsonEditModal
                  currentRule={currentRule}
                  handleCloseModal={this.handleCloseModal}
                  handleConfirm={this.handleEditRule}
                  handleValidate={this.handleValidateRule}
                ></CustomRuleJsonEditModal>
              )}

              {isEditDataModalOpen && (
                <EditCustomFirstLookDataModal
                  networkId={networkId}
                  networkInfo={networkInfo}
                  units={units}
                  firstLookCurrency={firstLookCurrency}
                  virtualPlacements={virtualPlacements}
                  placements={placements}
                  currentData={customRules}
                  handleClose={() =>
                    this.setState({ isEditDataModalOpen: false })
                  }
                  handleDataChanged={this.handleEditCurrentConfig}
                ></EditCustomFirstLookDataModal>
              )}

              {isPreviewValidateModalOpen && (
                <PreviewValidateModal
                  type={CUSTOM_TYPE.FIRST_LOOK}
                  originalRules={originalRules}
                  currentData={customRules}
                  handleClose={() =>
                    this.setState({ isPreviewValidateModalOpen: false })
                  }
                  handleSave={(notesValue) => {
                    this.setState({ isPreviewValidateModalOpen: false });
                    this.handleSaveCustomFL(notesValue);
                  }}
                  handleValidate={this.handleValidate}
                ></PreviewValidateModal>
              )}

              {isPromptModalOpen && (
                <PromptModal
                  isOpenModal={isPromptModalOpen}
                  handleConfirm={promptAction}
                  handleCancel={() =>
                    this.setState({ isPromptModalOpen: false, promptMsg: [] })
                  }
                  msg={promptMsg}
                  header={promptHeader}
                  width={
                    promptHeader === "Replace Current Config"
                      ? "45%"
                      : undefined
                  }
                >
                  {promptMessage}
                </PromptModal>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

class ActionButtonList extends React.PureComponent {
  render() {
    const {
      selectedIds,
      handleSelectedRemove,
      handleSelectedEnable,
      handleSelectedDisable,
    } = this.props;
    return (
      <>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={handleSelectedEnable}
        >
          Enable selected
        </button>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={handleSelectedDisable}
        >
          Disable selected
        </button>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={handleSelectedRemove}
        >
          Remove selected
        </button>
      </>
    );
  }
}

export default NetworkFirstLookSettingViewer;
